import { appConfig } from '../../config/appConfig.js';

/**
 * CacheManager - Generic TTL-based caching utility
 *
 * Provides time-to-live (TTL) based caching with automatic expiration,
 * cache hit/miss metrics, and configurable storage limits.
 */
export class CacheManager {
  constructor(options = {}) {
    this.ttl = options.ttl || appConfig.cache.ttl;
    this.maxKeys = options.maxKeys || appConfig.cache.maxKeys;
    this.cache = new Map();
    this.metrics = {
      hits: 0,
      misses: 0,
      sets: 0,
      deletes: 0,
      evictions: 0
    };
  }

  /**
   * Store data in cache with TTL
   *
   * @param {string} key - Cache key
   * @param {any} data - Data to cache
   * @param {number} customTTL - Optional custom TTL in milliseconds
   */
  set(key, data, customTTL = null) {
    const ttl = customTTL || this.ttl;
    const expiresAt = Date.now() + ttl;

    // Evict oldest entries if cache is full
    if (this.cache.size >= this.maxKeys) {
      this._evictOldest();
    }

    this.cache.set(key, {
      data,
      expiresAt,
      createdAt: Date.now()
    });

    this.metrics.sets++;
  }

  /**
   * Retrieve data from cache
   *
   * @param {string} key - Cache key
   * @returns {any|null} Cached data or null if expired/missing
   */
  get(key) {
    const cached = this.cache.get(key);

    if (!cached) {
      this.metrics.misses++;
      return null;
    }

    // Check if expired
    if (Date.now() > cached.expiresAt) {
      this.cache.delete(key);
      this.metrics.misses++;
      this.metrics.deletes++;
      return null;
    }

    this.metrics.hits++;
    return cached.data;
  }

  /**
   * Check if key exists and is not expired
   *
   * @param {string} key - Cache key
   * @returns {boolean} True if key exists and is valid
   */
  has(key) {
    const cached = this.cache.get(key);

    if (!cached) {
      return false;
    }

    // Check if expired
    if (Date.now() > cached.expiresAt) {
      this.cache.delete(key);
      this.metrics.deletes++;
      return false;
    }

    return true;
  }

  /**
   * Remove specific key from cache
   *
   * @param {string} key - Cache key to remove
   * @returns {boolean} True if key was removed
   */
  delete(key) {
    const existed = this.cache.delete(key);
    if (existed) {
      this.metrics.deletes++;
    }
    return existed;
  }

  /**
   * Clear all cached data
   */
  clear() {
    const size = this.cache.size;
    this.cache.clear();
    this.metrics.deletes += size;
  }

  /**
   * Get cache statistics
   *
   * @returns {Object} Cache metrics and status
   */
  getStats() {
    const total = this.metrics.hits + this.metrics.misses;
    const hitRate = total > 0 ? (this.metrics.hits / total) * 100 : 0;

    return {
      size: this.cache.size,
      maxKeys: this.maxKeys,
      hitRate: hitRate.toFixed(2) + '%',
      metrics: { ...this.metrics }
    };
  }

  /**
   * Get all cache keys
   *
   * @returns {Array<string>} Array of cache keys
   */
  keys() {
    return Array.from(this.cache.keys());
  }

  /**
   * Get cache utilization percentage
   *
   * @returns {number} Utilization percentage (0-100)
   */
  getUtilization() {
    return (this.cache.size / this.maxKeys) * 100;
  }

  /**
   * Cleanup expired entries
   * Useful for periodic maintenance
   *
   * @returns {number} Number of expired entries removed
   */
  cleanup() {
    const now = Date.now();
    let removed = 0;

    for (const [key, cached] of this.cache.entries()) {
      if (now > cached.expiresAt) {
        this.cache.delete(key);
        removed++;
      }
    }

    this.metrics.deletes += removed;
    return removed;
  }

  /**
   * Evict oldest entry when cache is full
   *
   * @private
   */
  _evictOldest() {
    let oldestKey = null;
    let oldestTime = Infinity;

    for (const [key, cached] of this.cache.entries()) {
      if (cached.createdAt < oldestTime) {
        oldestTime = cached.createdAt;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.cache.delete(oldestKey);
      this.metrics.evictions++;
    }
  }
}

// Export singleton instance with default configuration
export const cacheManager = new CacheManager();